import HorizontalScroll from "@Components/HorizontalScroll";
import Subsection from "../Subsection";
import styles from "./ReferralProgram.module.scss";
import ReferralProgramCard from "./components/ReferralProgramCard";
import {
  AmberPlusOfferCards,
  CARD_STRIDE_DESKTOP,
  CARD_STRIDE_MOBILE,
  useAmberPlusOffers,
} from "../AmberPlusOffers";
import OffersTouchpoints from "../OffersTouchpoints";
import PoweredByAmberPlus from "../OffersTouchpoints/PoweredByAmberPlus";
import config from "@Config/index";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import {
  DESKTOP_APP_URL_HOME_PAGE_OFFER_SECTION,
  MOBILE_APP_URL_HOME_PAGE_OFFER_SECTION,
} from "constants/configConstants";
import useTranslationHook from "hooks/useTranslationHook";
import useIsLanguage from "hooks/useIsLanguage";
import wrapperHOC from "@Utils/wrapperHOC";
import getAppLink from "@Utils/getAppLink";
import useIsApp from "hooks/useIsApp";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { SCREEN_SIZE, useResponsiveness } from "hooks/useResponsiveness";
import { UIEvent, useRef } from "react";

// Global (EN) section copy from the Amber Plus design. Source-text keys: t() falls back to
// the key, so these read in English and pick up translations once they exist in Tolgee.
const SECTION_TITLE = "Move-In Ready";
const SECTION_SUBTITLE =
  "A dedicated amber buddy handles your move-in essentials and helps you avail exclusive student offers. All free!";

const ReferralProgram = ({ isDesktop = false }) => {
  const { t } = useTranslationHook();
  const language = useIsLanguage();
  const isApp = useIsApp();
  const { isAbove } = useResponsiveness();
  const isAboveMD = isAbove(SCREEN_SIZE.MD, false);
  const showForDesktop = isDesktop && isAboveMD;

  // Global EN gets the merged "Move-In Ready" rail; other langs keep the previous
  // referral/scholarship/app rail (Amber+ Offers was only ever shown for global EN).
  const showAmberPlus = !config.IS_CHINA_APP && (!language || language === "en");

  const scholarshipCardTitle = t("home.reffralprogram.scholarship.cardtitle");
  const scholarshipCardSubTitleOne = t("home.reffralprogram.scholarship.cardsubtitleone");
  const scholarshipCardSubTitleTwo = t("home.reffralprogram.scholarship.cardsubtitletwo");
  const scholarshipCardCTA = t("home.reffralprogram.scholarship.cardcta");
  const amberPlusTitle = t("home.reffralprogram.amberplus.title");
  const amberPlusSubTitleOne = t("home.reffralprogram.amberplus.subtitleone");
  const amberPlusSubTitleTwo = t("home.reffralprogram.amberplus.subtitletwo");
  const amberPlusCTA = t("home.reffralprogram.amberplus.cta");

  // --- Previous rail copy (other langs / China) ---
  const referSecTitle = t("home.reffralprogram.refferaltitle");
  const referSecSubTitle = t("home.reffralprogram.promotions.subtitle");
  const referCardTitleOne = t("home.reffralprogram.referralcard.titleone");
  const referCardSubtitleOne = t("home.reffralprogram.referralcard.subtitleone");
  const referCardSubtitleTwo = t("home.reffralprogram.referralcard.subtitletwo");
  const referCardCTA = t("home.reffralprogram.referralcard.cta");
  const appTitle = t("home.reffralprogram.appoffer.title");
  const appSubTitle = t("home.reffralprogram.appoffer.subtitle");
  const appCTA = t("home.reffralprogram.appoffer.cta");

  const amberPlusOffers = useAmberPlusOffers();

  // Promo cards are hand-written rather than mapped, so count them the same way they render:
  // Scholarships always, plus the amber+ card on the China app.
  const promoCardCount = 1 + (config.IS_CHINA_APP ? 1 : 0);
  const totalCards = promoCardCount + (showAmberPlus ? amberPlusOffers.length : 0);
  // Every card in the rail is now 356px on desktop (314px on mobile), so a single stride
  // steps the carousel arrows exactly one card at a time.
  const cardStride = showForDesktop ? CARD_STRIDE_DESKTOP : CARD_STRIDE_MOBILE;
  const viewedCardDepthRef = useRef(1);

  const handleCarouselScroll = (event: UIEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (!target || !totalCards) return;
    const visibleCard = Math.min(
      totalCards,
      Math.max(1, Math.ceil((target.scrollLeft + target.clientWidth) / cardStride)),
    );
    if (visibleCard <= viewedCardDepthRef.current) return;
    viewedCardDepthRef.current = visibleCard;
    gtagEventEmitter({
      action: "amber_plus_carousel_scroll",
      scroll_depth: `viewed ${visibleCard} out of ${totalCards} cards`,
    });
  };

  const scholarshipSubtitle = config.IS_CHINA_APP ? (
    <>
      {scholarshipCardSubTitleOne}
      <br />
      {scholarshipCardSubTitleTwo}
    </>
  ) : (
    <>
      {scholarshipCardSubTitleOne} <br />
      {scholarshipCardSubTitleTwo}
    </>
  );

  // Other langs / China: previous "Amber Referral Program And Offers" rail.
  if (!showAmberPlus) {
    return (
      <Subsection
        title={referSecTitle}
        subtitle={referSecSubTitle}
        withSidePadding={showForDesktop}
        subSectionPaddingReduced={!showForDesktop}
        greyBg
        isDesktop={showForDesktop}
      >
        {showForDesktop ? (
          <HorizontalScroll
            showControls
            className={formatClassNames(styles.horizontalScroll, showForDesktop && styles.desktop)}
          >
            <ReferralProgramCard
              index={1}
              testId="Refer"
              title={<>{referCardTitleOne}</>}
              gtagTitle="Refer a friend, and you both get £50"
              subtitle={
                <>
                  {referCardSubtitleOne}
                  <br />
                  {referCardSubtitleTwo}
                </>
              }
              ctaText={referCardCTA}
              href="/referral-programme?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-01.png?q=90`}
              isDesktop
            />
            <ReferralProgramCard
              index={2}
              testId="Scholarships"
              title={scholarshipCardTitle}
              gtagTitle="Unlock up to $15,000 in Scholarships!"
              subtitle={scholarshipSubtitle}
              ctaText={scholarshipCardCTA}
              href="/scholarship?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-04.png?q=90`}
              isDesktop
              openInExternalBrowser
            />
            {config.IS_CHINA_APP && (
              <ReferralProgramCard
                index={3}
                testId="Amber-plus"
                title={
                  language !== "en" ? (
                    amberPlusTitle
                  ) : (
                    <>
                      Save up to <span className={styles.redText}>£1500</span> with amber
                      <span className={styles.redText}>+</span>
                    </>
                  )
                }
                gtagTitle="Save up to £1500 with amber+"
                subtitle={
                  <>
                    {amberPlusSubTitleOne} <br /> {amberPlusSubTitleTwo}
                  </>
                }
                ctaText={amberPlusCTA}
                href="/plus?utm_source=home_page"
                bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-03.png?q=90`}
                isDesktop
              />
            )}
            <ReferralProgramCard
              index={4}
              testId="App"
              title={
                config.IS_CHINA_APP ? (
                  appTitle
                ) : (
                  <>
                    {t("home.reffralprogram.appoffer.titleone")}
                    <br />
                    {t("home.reffralprogram.appoffer.titletwo")}
                  </>
                )
              }
              gtagTitle="Get additional £20 cashback on your booking!"
              subtitle={
                config.IS_CHINA_APP ? (
                  appSubTitle
                ) : (
                  <>
                    {t("home.reffralprogram.appoffer.subtitleone")} <br />
                    {t("home.reffralprogram.appoffer.subtitletwo")}
                  </>
                )
              }
              ctaText={appCTA}
              href={getAppLink(DESKTOP_APP_URL_HOME_PAGE_OFFER_SECTION)}
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-02.png?q=90`}
              isDesktop
            />
          </HorizontalScroll>
        ) : (
          <HorizontalScroll className={styles.horizontalScroll}>
            <ReferralProgramCard
              testId="Refer"
              index={1}
              title={<>{referCardTitleOne}</>}
              gtagTitle="Refer a friend, and you both get £50"
              subtitle={
                <>
                  {referCardSubtitleOne}
                  <br />
                  {referCardSubtitleTwo}
                </>
              }
              ctaText={referCardCTA}
              href="/referral-programme?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer1.png?q=90`}
            />
            <ReferralProgramCard
              testId="Scholarships"
              ctaText={scholarshipCardCTA}
              index={2}
              title={scholarshipCardTitle}
              gtagTitle="Unlock up to $15,000 in Scholarships!"
              subtitle={scholarshipSubtitle}
              openInExternalBrowser={isApp}
              getLocalizedPath
              href="/scholarship?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer4.png?q=90`}
            />
            {config.IS_CHINA_APP && (
              <ReferralProgramCard
                testId="Amber-plus"
                index={3}
                title={
                  language !== "en" ? (
                    amberPlusTitle
                  ) : (
                    <>
                      Save up to <span className={styles.redText}>£300</span> with amber
                      <span className={styles.redText}>+</span>
                    </>
                  )
                }
                gtagTitle="Save up to £300 with amber+"
                subtitle={
                  <>
                    {amberPlusSubTitleOne} <br /> {amberPlusSubTitleTwo}
                  </>
                }
                ctaText={amberPlusCTA}
                href="/plus?utm_source=home_page"
                bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer3.png?q=90`}
                openInExternalBrowser={isApp}
                getLocalizedPath
              />
            )}
            {!isApp && (
              <ReferralProgramCard
                testId="App"
                index={4}
                title={
                  config.IS_CHINA_APP ? (
                    appTitle
                  ) : (
                    <>
                      {t("home.reffralprogram.appoffer.titleone")}
                      <br />
                      {t("home.reffralprogram.appoffer.titletwo")}
                    </>
                  )
                }
                gtagTitle="Get additional £20 cashback on your booking!"
                subtitle={
                  config.IS_CHINA_APP ? (
                    appSubTitle
                  ) : (
                    <>
                      {t("home.reffralprogram.appoffer.subtitleone")} <br />
                      {t("home.reffralprogram.appoffer.subtitletwo")}
                    </>
                  )
                }
                ctaText={appCTA}
                href={getAppLink(MOBILE_APP_URL_HOME_PAGE_OFFER_SECTION)}
                bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer2.png?q=90`}
              />
            )}
          </HorizontalScroll>
        )}
      </Subsection>
    );
  }

  // Global EN: merged "Move-In Ready" section with amber+ touchpoints + partner cards.
  const amberPlusCards = (
    <AmberPlusOfferCards offers={amberPlusOffers} showForDesktop={showForDesktop} />
  );
  const poweredBy = showForDesktop ? <PoweredByAmberPlus /> : null;

  return (
    <Subsection
      title={t(SECTION_TITLE)}
      subtitle={t(SECTION_SUBTITLE)}
      withSidePadding={showForDesktop}
      subSectionPaddingReduced={!showForDesktop}
      greyBg
      isDesktop={showForDesktop}
      headerAside={poweredBy}
      noMarginBottom
    >
      <div className={styles.sectionBody}>
        <OffersTouchpoints />
        {showForDesktop ? (
          <HorizontalScroll
            showControls
            controlsClass={styles.railControls}
            itemSize={cardStride}
            onHorizontalScroll={handleCarouselScroll}
            className={formatClassNames(styles.horizontalScroll, showForDesktop && styles.desktop)}
          >
            {amberPlusCards}
            <ReferralProgramCard
              index={2}
              testId="Scholarships"
              title={scholarshipCardTitle}
              gtagTitle="Unlock up to $15,000 in Scholarships!"
              subtitle={scholarshipSubtitle}
              ctaText={scholarshipCardCTA}
              href="/scholarship?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/Offer-Card-04.png?q=90`}
              isDesktop
              openInExternalBrowser
            />
          </HorizontalScroll>
        ) : (
          <HorizontalScroll
            itemSize={cardStride}
            onHorizontalScroll={handleCarouselScroll}
            className={styles.horizontalScroll}
          >
            {amberPlusCards}
            <ReferralProgramCard
              testId="Scholarships"
              ctaText={scholarshipCardCTA}
              index={2}
              title={scholarshipCardTitle}
              gtagTitle="Unlock up to $15,000 in Scholarships!"
              subtitle={scholarshipSubtitle}
              openInExternalBrowser={isApp}
              getLocalizedPath
              href="/scholarship?utm_source=home_page"
              bgSrc={`${config.IMAGE_STATIC_ASSETS_APP_PATH}/assets/refer4.png?q=90`}
            />
          </HorizontalScroll>
        )}
      </div>
    </Subsection>
  );
};

export default wrapperHOC(ReferralProgram, {
  componentName: "ReferralProgram-Home",
  showForChina: true,
  showForMiniProgram: false,
});
